import type { TradeCycleVersion } from '@festina/unitlink-general-types/unitlink'
import { notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useHook } from '../../lenteHooks'
import { assertNoDuplicateTradeCycleIds } from './assertNoDuplicateTradeCycleIds'
import {
  type TradeCycleFilterFormValues,
  tradeCycleFilterValuesToParams,
} from './tradeCycleFilterUtils'

export const useGetTradeCycleChains = (
  filterValues: TradeCycleFilterFormValues,
) => {
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { data } = useHook('fetchers.unitlink.useFetchTradeCycles')({
    tenantId,
    ...tradeCycleFilterValuesToParams(filterValues),
  })

  return useMemo(() => {
    assertNoDuplicateTradeCycleIds(data?.tradeCycleVersions)
    // The rest of the code assumes there are no duplicate tradeCycleId.

    // Index of each cycle keyed by its `previousTradeCycleId`, allowing us to
    // look up the cycle that immediately follows a given cycle in its chain.
    const previousCycleIdToTradeCycle: Record<string, TradeCycleVersion> = {}

    // The "root" (oldest) cycle of each chain, i.e. any cycle that
    // doesn't have a predecessor (or a `previousTradeCycleId` property).
    const rootTradeCycles: TradeCycleVersion[] = []

    const tradeCycleIds = new Set(
      data?.tradeCycleVersions
        ?.map((tradeCycleVersion) => tradeCycleVersion?.tradeCycleId)
        .filter(notEmpty),
    )

    data?.tradeCycleVersions?.forEach((tradeCycleVersion) => {
      if (!tradeCycleVersion?.tradeCycleId) {
        return
      } else if (
        tradeCycleVersion.previousTradeCycleId &&
        // By default, we show the last 31 days, so it can happen that the previous cycle is not included in the fetched data.
        tradeCycleIds.has(tradeCycleVersion.previousTradeCycleId)
      ) {
        previousCycleIdToTradeCycle[tradeCycleVersion.previousTradeCycleId] =
          tradeCycleVersion
      } else {
        rootTradeCycles.push(tradeCycleVersion)
      }
    })

    // For each chain, walk forward from its root cycle to the most recent one,
    // building an array of cycles in oldest-to-most-recent order that gets reversed before returning.
    return rootTradeCycles.map((rootTradeCycle) => {
      const cycles: TradeCycleVersion[] = []
      let currCycle: Optional<TradeCycleVersion> = rootTradeCycle

      while (currCycle?.tradeCycleId) {
        cycles.push(currCycle)
        currCycle = previousCycleIdToTradeCycle[currCycle.tradeCycleId]
      }

      return cycles.reverse()
    })
  }, [data?.tradeCycleVersions])
}
